import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

// Function to clean and format the destination address for a Google Maps link
const cleanAddress = (address) => {
  // Replace problematic characters with simpler ones
  return address.replace(/[, ]+/g, '+'); // Replace commas and multiple spaces with '+'
};

// Function to create a Google Maps link for directions with specified destination
const getGoogleMapsDirectionsLink = (destAddress, destLat, destLong) => {
  // Clean the destination address and ensure proper formatting
  const formattedDestAddress = cleanAddress(destAddress);

  // Construct the Google Maps link for directions with specified destination
  return `https://www.google.com/maps/dir//${formattedDestAddress}/@${destLat},${destLong}/`;
};

// Component to initialize Google Maps and add markers with info windows
const CustomGoogleMap = ({ pins }) => {
  const mapContainerRef = useRef(null); // Reference for the map container
  const mapRef = useRef(null); // Reference for the map instance
  const [infoWindow, setInfoWindow] = useState(null); // Reference for the InfoWindow
  const [selectedPin, setSelectedPin] = useState(null); // State for selected pin

  const initializeMap = () => {
    const mapOptions = {
      zoom: 10, // Default zoom level
      disableDefaultUI: true, // Hide all default controls
      mapId: 'DEMO_MAP_ID', // Map ID is required for advanced markers.
    };

    const map = new google.maps.Map(mapContainerRef.current, mapOptions);
    mapRef.current = map; // Store the map instance

    // Create LatLngBounds to fit all pins
    const bounds = new google.maps.LatLngBounds();

    // Add markers to the map
    pins.forEach((pin) => {
      const position = { lat: pin.latitude, lng: pin.longitude };

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position,
        map: mapRef.current,
        title: pin.company,
      });

      // Extend bounds to include this pin's position
      bounds.extend(position);

      // Create and set up the InfoWindow
      const infoWindowInstance = new google.maps.InfoWindow();
      setInfoWindow(infoWindowInstance); // Store the InfoWindow instance

      // Marker click event to open the InfoWindow
      marker.addListener('click', () => {
        // Get the full address for the destination
        const destAddress = `${pin.company}, ${pin.address}, ${pin.city}, ${pin.state} ${pin.postal_code}`;

        // Generate the Google Maps link for directions with specified destination
        const googleMapsLink = getGoogleMapsDirectionsLink(
          destAddress,
          pin.latitude,
          pin.longitude
        );

        infoWindowInstance.setContent(
          `<div><h4>${pin.company}</h4>
          <p>${pin.address}, ${pin.city}, ${pin.state}, ${pin.postal_code}</p>
          <p><a href="tel:${pin.phone}">${pin.phone}</a></p>
          <a href="${googleMapsLink}" target="_blank">Get Directions</a>
          </div>`
        );
        infoWindowInstance.open(map, marker); // Open the InfoWindow on this marker
        setSelectedPin(pin); // Store the selected pin
      });
    });

    // Adjust the map to fit all pins
    map.fitBounds(bounds);
  };

  useEffect(() => {
    initializeMap();
  }, [pins]); // Re-run if dependencies change

  return (
    <div className='gmap-wrapper'>
      <div className='gmap' ref={mapContainerRef}></div> {/* Map container */}
    </div>
  );
};

export default CustomGoogleMap;

CustomGoogleMap.propTypes = {
  pins: PropTypes.array.isRequired, // Validate as an array
};
